/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from "react";
import { hexToHslTriplet } from "@/lib/hexToHsl";
import { useBrandingQuery, useBrandingMutation } from "@/api/hooks/useBranding";
import type { BrandingApiResponse } from "@/api/hooks/useBranding";

/** API response shape (snake_case from backend). */
export type { BrandingApiResponse };

export interface BrandingSettings {
  appName: string;
  appTagline: string;
  appDescription: string;
  stateName: string;
  stateCode: string;
  agencyName: string;
  agencyAbbreviation: string;
  logoLight: string;
  logoDark: string;
  logoIcon: string;
  favicon: string;
  primaryColor: string;
  primaryColorLight: string;
  primaryColorDark: string;
  accentColor: string;
  footerText: string;
  copyrightText: string;
  supportEmail: string;
  supportPhone: string;
  helpTitle: string;
  helpIntro: string;
  showPoweredBy: boolean;
}

function apiToBranding(r: BrandingApiResponse): BrandingSettings {
  return {
    appName: r.app_name,
    appTagline: r.app_tagline ?? "",
    appDescription: r.app_description ?? "",
    stateName: r.state_name ?? "",
    stateCode: r.state_code,
    agencyName: r.agency_name ?? "",
    agencyAbbreviation: r.agency_abbreviation ?? "",
    logoLight: r.logo_light ?? "",
    logoDark: r.logo_dark ?? "",
    logoIcon: r.logo_icon ?? "",
    favicon: r.favicon ?? "",
    primaryColor: r.primary_color,
    primaryColorLight: r.primary_color_light,
    primaryColorDark: r.primary_color_dark,
    accentColor: r.accent_color,
    footerText: r.footer_text ?? "",
    copyrightText: r.copyright_text ?? "",
    supportEmail: r.support_email ?? "",
    supportPhone: r.support_phone ?? "",
    helpTitle: r.help_title ?? "",
    helpIntro: r.help_intro ?? "",
    showPoweredBy: r.show_powered_by,
  };
}

function brandingToApi(b: BrandingSettings): Record<string, unknown> {
  return {
    app_name: b.appName,
    app_tagline: b.appTagline || null,
    app_description: b.appDescription || null,
    help_title: b.helpTitle || null,
    help_intro: b.helpIntro || null,
    support_email: b.supportEmail || null,
    support_phone: b.supportPhone || null,
    state_code: b.stateCode || "global",
    state_name: b.stateName || null,
    agency_name: b.agencyName || null,
    agency_abbreviation: b.agencyAbbreviation || null,
    // No `|| null` on the four logo fields, unlike the text ones above. The backend distinguishes
    // "" (clear this logo) from absent (leave it alone); collapsing "" to null erased the first of
    // those, and the Remove button became a silent no-op -- success toast, logo still there after
    // a reload. Form state is always a string (apiToBranding maps null to ""), so null never
    // carried information the backend needed.
    logo_light: b.logoLight,
    logo_dark: b.logoDark,
    logo_icon: b.logoIcon,
    favicon: b.favicon,
    primary_color: b.primaryColor,
    primary_color_light: b.primaryColorLight,
    primary_color_dark: b.primaryColorDark,
    accent_color: b.accentColor,
    footer_text: b.footerText || null,
    copyright_text: b.copyrightText || null,
    show_powered_by: b.showPoweredBy,
  };
}

/**
 * The subset of `next` that differs from what the server currently holds.
 *
 * `BrandingUpdate` is documented as "all optional for partial update", and the backend treats an
 * absent field as "leave it alone" -- so sending the whole form every time throws that away and
 * makes every save a full overwrite. It matters because of approvals: a change request stores
 * `model_dump(exclude_none=True)` and replays it when someone approves, possibly days later. A
 * colour-only request that carried the logos as they stood when it was raised would revert any
 * logo uploaded in the meantime. Sending only what changed makes "not mentioned" mean exactly
 * that, which is what the backend guard already assumes.
 *
 * `previous` is deliberately non-nullable. Falling back to the full payload when nothing is loaded
 * looks harmless but is not: the draft is `DEFAULT_BRANDING` at that point, whose logo fields are
 * the shipped asset paths rather than empty strings, so the payload would quietly overwrite a
 * deployment's uploaded logos with `/images/torqe-logo.svg` and friends. `saveBranding` refuses the
 * save instead -- see the guard there.
 */
function changedBrandingFields(
  next: BrandingSettings,
  previous: BrandingSettings
): Record<string, unknown> {
  const full = brandingToApi(next);
  const before = brandingToApi(previous);
  return Object.fromEntries(
    Object.entries(full).filter(([key, value]) => !Object.is(value, before[key]))
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export const DEFAULT_BRANDING: BrandingSettings = {
  appName: "TorQE",
  appTagline: "T-MSIS Outcome Review & Quality Engine",
  appDescription:
    "Streamline your T-MSIS data quality management with intelligent validation, real-time analytics, and automated compliance tracking.",
  // State identity comes from the backend (branding API mirrors config.state_config).
  // Neutral pre-load placeholder — never hardcode a specific state; overridden by API.
  stateName: "",
  stateCode: "",
  agencyName: "",
  agencyAbbreviation: "",
  logoLight: "/images/torqe-logo.svg",
  logoDark: "/images/torqe-logo-white.svg",
  logoIcon: "/images/torqe-icon.svg",
  favicon: "/images/torqe-favicon.svg",
  /* Default aligns with index.css --primary (hsl 23 100% 39% ≈ #c74c00); matches text-primary / KPI fail % */
  primaryColor: "#c74c00",
  primaryColorLight: "#fed7aa",
  primaryColorDark: "#a84100",
  accentColor: "#2563eb",
  footerText: "T-MSIS Data Quality Management Platform",
  copyrightText: `© ${String(new Date().getFullYear())} All rights reserved.`,
  supportEmail: "support@example.gov",
  supportPhone: "(555) 123-4567",
  helpTitle: "Help & Documentation",
  helpIntro:
    "Welcome to the help center. Find answers to common questions and learn how to use the platform effectively.",
  showPoweredBy: true,
};

interface BrandingContextType {
  branding: BrandingSettings;
  updateBranding: (updates: Partial<BrandingSettings>) => void;
  resetBranding: () => void;
  /** Save current branding to API. Returns changeRequestNumber when a change request was created (non-admin). */
  saveBranding: (overrides?: BrandingSettings) => Promise<{ changeRequestNumber?: string }>;
  isLoading: boolean;
  hasChanges: boolean;
  /** Request number of a submitted-but-unapproved change, or null. The draft is deliberately kept
   *  after such a save, so `hasChanges` stays true and cannot by itself tell "edited" from
   *  "already submitted"; callers gate their Save control on this to avoid filing a duplicate. */
  pendingRequestNumber: string | null;
}

const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

export function BrandingProvider({ children }: { children: ReactNode }) {
  const { data: apiData, isLoading: queryLoading } = useBrandingQuery();
  const mutation = useBrandingMutation();

  const savedBranding = useMemo(() => (apiData ? apiToBranding(apiData) : null), [apiData]);
  const [draft, setDraft] = useState<BrandingSettings>(DEFAULT_BRANDING);
  const lastSyncedServerRef = useRef<string | null>(null);

  // Set when a save became a change request rather than applying directly. Keeping the draft (so
  // the user's edits survive the save) means `hasChanges` stays true afterwards -- the server value
  // has not moved -- which on its own would leave Save live and let a second press file a second,
  // identical request. Nothing downstream dedupes: `router_config.py` creates and submits
  // unconditionally, and neither `create_change_request` nor `submit_for_review` looks for an
  // existing pending `ui_branding` request.
  const [pendingRequestNumber, setPendingRequestNumber] = useState<string | null>(null);

  // Sync draft from server whenever server data changes (e.g. State Configuration update) so
  // System Settings reflects changes immediately and doesn't show false "unsaved changes"
  useEffect(() => {
    if (!savedBranding) return;
    const serverKey = JSON.stringify(savedBranding);
    if (lastSyncedServerRef.current !== serverKey) {
      lastSyncedServerRef.current = serverKey;
      setDraft(savedBranding);
      // The stored branding moved, so any request that was pending against the old value has been
      // approved (or overtaken). Either way it no longer blocks proposing something new.
      setPendingRequestNumber(null);
    }
  }, [savedBranding]);

  // Use draft for display; if no saved data yet, show draft (initialized to DEFAULT_BRANDING)
  const branding = draft;
  const originalBranding = savedBranding ?? DEFAULT_BRANDING;
  const hasChanges = JSON.stringify(branding) !== JSON.stringify(originalBranding);

  const isLoading = queryLoading;

  // Apply branding to document (title, CSS vars, favicon)
  useEffect(() => {
    const root = document.documentElement;

    // The `--brand-*` pair is the stable, hex-valued surface for `custom_css`. It is NOT what
    // themes the app: nothing in src/ or tailwind.config.ts reads it, which is why saving a colour
    // used to change nothing outside the Login gradient and the settings preview.
    root.style.setProperty("--brand-primary", branding.primaryColor);
    root.style.setProperty("--brand-primary-light", branding.primaryColorLight);
    root.style.setProperty("--brand-primary-dark", branding.primaryColorDark);
    root.style.setProperty("--brand-accent", branding.accentColor);

    // These are the ones the UI actually renders from. index.css declares them as bare HSL
    // triplets and tailwind.config.ts wraps them -- `hsl(var(--primary))` -- so they must be
    // written in that form; a hex value here yields `hsl(#c74c00)` and is silently ignored.
    // An unparseable colour leaves the stylesheet's own value in place rather than blanking it.
    // Returns what it wrote, so a caller that needs the triplet as well does not convert twice.
    const setHsl = (name: string, hex: string | null | undefined): string | null => {
      const triplet = hexToHslTriplet(hex);
      if (triplet) {
        root.style.setProperty(name, triplet);
      } else {
        // Remove rather than leave the previous value in place. These are inline styles on <html>,
        // which outrank the stylesheet -- the same property that lets a brand colour apply in dark
        // mode also means a stale one can never be overridden by index.css. Clearing a colour has
        // to clear the override, or the old brand colour survives until a hard reload and there is
        // no way back to the defaults.
        root.style.removeProperty(name);
      }
      return triplet;
    };
    // --primary-foreground moves WITH --primary, or the pair comes apart. index.css hardcodes it
    // to pure white in both themes (:15, :56) and button.tsx puts them on one element
    // ("bg-primary text-primary-foreground"). That was safe only while --primary stayed the
    // built-in orange; now that an admin's colour drives it, a pale brand would render every
    // primary button, badge and chart label white on near-white. DEFAULT_BRANDING's own
    // primaryColorLight (#fed7aa) is 83.1% lightness, so this is reachable from the shipped
    // palette, and the colour picker gives no warning.
    //
    // The dark value is index.css's own --accent-foreground (:23), so this stays inside the
    // existing palette rather than introducing a colour. The threshold is a judgement: white text
    // starts failing WCAG AA against these hues somewhere around 55%, so 60 is the conservative
    // side of it.
    const setHslWithForeground = (name: string, hex: string | null | undefined) => {
      const triplet = setHsl(name, hex);
      if (!triplet) {
        // Removed alongside the colour, for the reason documented on setHsl: an inline style on
        // <html> outranks the stylesheet, so a stale foreground could not be overridden back.
        root.style.removeProperty(`${name}-foreground`);
        return;
      }
      const lightness = Number(triplet.split(" ")[2].replace("%", ""));
      root.style.setProperty(
        `${name}-foreground`,
        Number.isFinite(lightness) && lightness > 60 ? "222.2 47.4% 11.2%" : "0 0% 100%"
      );
    };

    setHslWithForeground("--primary", branding.primaryColor);
    setHsl("--primary-border", branding.primaryColorDark);
    // Focus rings are keyed to the brand in index.css (--ring repeats --primary), so they have to
    // move together or every focused control keeps the old orange.
    setHsl("--ring", branding.primaryColor);

    // `--accent` is deliberately NOT written. Despite the name it is a near-white hover background
    // in this theme (index.css: 210 40% 96.1%) paired with a dark --accent-foreground, so putting
    // a saturated brand colour there makes hover states dark-on-dark. The brand accent stays
    // available as --brand-accent above.
    //
    // Inline styles on <html> outrank the stylesheet's `.dark` block, so the branded primary
    // applies in dark mode too rather than reverting to the built-in dark orange.

    if (branding.favicon) {
      const link = document.querySelector("link[rel~='icon']");
      if (link) (link as HTMLLinkElement).href = branding.favicon;
    }
    document.title = branding.appName;
  }, [branding]);

  const updateBranding = useCallback((updates: Partial<BrandingSettings>) => {
    setDraft((prev) => ({ ...prev, ...updates }));
    // The draft no longer matches what was submitted, so the pending request no longer describes
    // what the user is asking for. Proposing again is a legitimate act rather than a double-click.
    setPendingRequestNumber(null);
  }, []);

  const resetBranding = useCallback(() => {
    setDraft(originalBranding);
    setPendingRequestNumber(null);
  }, [originalBranding]);

  const saveBranding = useCallback(
    async (overrides?: BrandingSettings): Promise<{ changeRequestNumber?: string }> => {
      const toSave = overrides ?? branding;

      // Nothing loaded means the form is showing DEFAULT_BRANDING, not this deployment's settings,
      // so every field on it is a placeholder rather than something the user chose to keep. Saving
      // that would write the shipped defaults -- logo paths included -- over whatever is actually
      // stored. Both callers wrap this in a try/catch with a destructive toast, so throwing puts
      // the user in front of an error instead of silently replacing their branding.
      if (!savedBranding) {
        throw new Error("Branding settings have not loaded yet.");
      }

      // Guarded here rather than on the Save button alone, for the reason the empty-delta guard
      // below is: there are two callers and a control-level guard only covers the one it sits on.
      // "Reset to Defaults" is gated purely on its own isResetting flag, so with the check only on
      // Save it could still file a second request and orphan the first in the approver's queue.
      // Editing any field clears this, so proposing something genuinely different stays one click.
      if (pendingRequestNumber !== null) {
        throw new Error("A previous change is still awaiting approval.");
      }

      const body = changedBrandingFields(toSave, savedBranding);

      // Refuse the round trip, not just the payload. Sending only the delta makes an empty body
      // representable, and nothing downstream turns one away: the approval path passes it to
      // `proposed_values=body.model_dump(exclude_none=True)` and submits it, so an approver gets a
      // change request that proposes nothing. Save cannot reach this (it is gated on hasChanges)
      // but "Reset to Defaults" can -- it is gated only on isResetting and saves DEFAULT_BRANDING
      // unconditionally, so on a deployment already at the shipped defaults it sends {}. Guarded
      // here rather than on that button so any future caller inherits it.
      if (Object.keys(body).length === 0) return {};

      const data = await mutation.mutateAsync(body);
      const submittedForApproval = data.change_request_id != null && data.request_number != null;

      // Whether the save applied directly or became a change request is decided server-side by
      // role, so a user without direct-apply rights gets back the CURRENT branding -- not what
      // they just submitted. Adopting that response would wipe their edits out of the form the
      // moment they pressed Save, which reads as "the save silently reverted". Keep the draft
      // instead: it is still what they asked for, and it is what the pending request will apply.
      if (!submittedForApproval) {
        const fromApi = apiToBranding(data);
        setDraft({
          ...fromApi,
          showPoweredBy:
            typeof data.show_powered_by === "boolean" ? data.show_powered_by : toSave.showPoweredBy,
        });
      }

      if (submittedForApproval) {
        setPendingRequestNumber(data.request_number ?? null);
        return { changeRequestNumber: data.request_number ?? undefined };
      }
      return {};
    },
    [branding, mutation, savedBranding, pendingRequestNumber]
  );

  return (
    <BrandingContext.Provider
      value={{
        branding,
        updateBranding,
        resetBranding,
        saveBranding,
        isLoading,
        hasChanges,
        pendingRequestNumber,
      }}
    >
      {children}
    </BrandingContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useBranding() {
  const context = useContext(BrandingContext);
  if (context === undefined) {
    throw new Error("useBranding must be used within a BrandingProvider");
  }
  return context;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useBrandingValues() {
  const { branding } = useBranding();
  return branding;
}
